import React, { useState } from "react";
import { useDeviceStore } from "../../store/useDeviceStore";
import {
  ServerIcon,
  KeyIcon,
  HashtagIcon,
  CpuChipIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { WifiIcon, ShieldCheckIcon } from "@heroicons/react/24/solid";

interface ConfigurationWidgetProps {
  isEditMode?: boolean;
  deviceId?: string;
}

export default function ConfigurationWidget({
  isEditMode = false,
  deviceId,
}: ConfigurationWidgetProps) {
  const { selectedDeviceId } = useDeviceStore();
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const resolvedDeviceId = deviceId || selectedDeviceId;

  // Publish-only device user (osmoair-device): ACL-limited to writing osmoair/#.
  // Deliberately NOT the privileged 'osmoair' backend user — this value ships in the public bundle.
  const mqttConfig = {
    host: "mqtts.osmoair.com",
    port: "8883",
    username: "osmoair-device",
    password: "bd3ec4a8d959ffdadfb718652c8e1443",
    topic: `osmoair/${resolvedDeviceId || "device_id"}`,
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
      {/* Header - Matching LiveTable Style */}
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                MQTT Configuration
              </h3>
              <p className="text-sm text-gray-500">
                Connection settings and credentials
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span>Connected</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
              <ShieldCheckIcon className="h-4 w-4" />
              <span>Secure</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Matching LiveTable Style */}
      <div className="px-6 py-4 space-y-6">
        {/* Connection Settings - All fields in one box */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">
              Connection Settings
            </h4>
            <div className="grid grid-cols-2 gap-6">
              {/* Host */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                    <ServerIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    Host
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white hover:bg-slate-100 transition-all duration-200 p-3 rounded-lg border border-gray-100">
                  <span className="text-sm font-mono text-gray-900 flex-1">
                    {mqttConfig.host}
                  </span>
                  <button
                    onClick={() => copyToClipboard(mqttConfig.host, "host")}
                    className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-all duration-200 border border-blue-200 hover:border-blue-300"
                    title="Copy host"
                  >
                    {copiedField === "host" ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Port */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                    <span className="text-sm font-bold text-green-600">🔌</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    Port
                  </span>
                </div>
                <div className="flex items-center gap-3 bg-white hover:bg-slate-100 transition-all duration-200 p-3 rounded-lg border border-gray-100">
                  <span className="text-sm font-mono text-gray-900 flex-1">
                    {mqttConfig.port}
                  </span>
                  <span className="px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-medium">
                    SSL
                  </span>
                </div>
              </div>

              {/* Username */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                    <KeyIcon className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    Username
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white hover:bg-slate-100 transition-all duration-200 p-3 rounded-lg border border-gray-100">
                  <span className="text-sm font-mono text-gray-900 flex-1">
                    {mqttConfig.username}
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(mqttConfig.username, "username")
                    }
                    className="p-1.5 rounded-md hover:bg-purple-50 text-purple-600 hover:text-purple-700 transition-all duration-200 border border-purple-200 hover:border-purple-300"
                    title="Copy username"
                  >
                    {copiedField === "username" ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Password */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                    <span className="text-sm font-bold text-orange-600">
                      🔒
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    Password
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white hover:bg-slate-100 transition-all duration-200 p-3 rounded-lg border border-gray-100">
                  <span className="text-sm font-mono text-gray-900 flex-1">
                    {showPassword ? mqttConfig.password : "••••••••••"}
                  </span>
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-700 transition-all duration-200 border border-gray-200 hover:border-gray-300"
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() =>
                      copyToClipboard(mqttConfig.password, "password")
                    }
                    className="p-1.5 rounded-md hover:bg-orange-50 text-orange-600 hover:text-orange-700 transition-all duration-200 border border-orange-200 hover:border-orange-300"
                    title="Copy password"
                  >
                    {copiedField === "password" ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* MQTT Topic - Full Width */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
             
              <span className="text-sm font-semibold text-gray-900">Topic</span>
              <span className="px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-medium">
                Active
              </span>
            </div>
            <button
              onClick={() => copyToClipboard(mqttConfig.topic, "topic")}
              className="p-1.5 rounded-md hover:bg-indigo-50 text-indigo-600 hover:text-indigo-700 transition-all duration-200 border border-indigo-200 hover:border-indigo-300"
              title="Copy topic"
            >
              {copiedField === "topic" ? (
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
              ) : (
                <ClipboardDocumentIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          <div className="text-sm font-mono text-gray-900 bg-slate-50 hover:bg-slate-100 transition-all duration-200 p-3 rounded-lg border border-gray-100 break-all">
            {mqttConfig.topic}
          </div>
        </div>
      </div>

      {/* Footer - Matching LiveTable Style */}
      <div className="bg-gray-50/50 border-t border-gray-100 px-6 py-3">
        <div className="flex items-center justify-between text-sm text-gray-900">
          <div className="flex items-center gap-4">
            <span className="font-medium">Configuration Status</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs">Connected</span>
              </div>
              <div className="flex items-center gap-1">
                <ShieldCheckIcon className="w-3 h-3 text-blue-600" />
                <span className="text-xs">Secure</span>
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-900">MQTT v3.1.1 over SSL</div>
        </div>
      </div>
    </div>
  );
}
